<template>
  <v-toolbar
    flat
    :color="`grey lighten-3`"
    ref="mapToolbar"
    class="map-toolbar"
  >
    <v-btn
      flat
      :icon="$vuetify.breakpoint.smAndDown"
      @click="toggleSidebar()"
      :color="isSidebarVisible ? 'primary' : 'grey darken-1'"
    >
      <v-icon>layers</v-icon> <span class="hidden-sm-and-down">שכבות</span>
    </v-btn>
    <!-- <v-spacer /> -->
    <v-autocomplete
      class="ml-2 mr-2"
      label="חיפוש מקום, כתובת, בית עסק או נ״צ..."
      solo
      clearable
      hide-details
      no-filter
      :items="items"
      item-text="title"
      item-value="_id"
      v-model="select"
      return-object
      :loading="searching"
      :search-input.sync="query"
      dir="auto"
    >
      <template v-slot:no-data>
        <v-list-tile>
          <v-list-tile-title>
            אין תוצאות
          </v-list-tile-title>
        </v-list-tile>
      </template>
      <template v-slot:selection="{ item, selected }">
        <span>{{ item.title }}</span>
      </template>
      <template v-slot:item="{ item }">
        <v-list-tile-avatar
          color="primary"
          class="headline font-weight-light white--text"
        >
          <v-icon color="white">{{ item.icon }}</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title v-text="item.title"></v-list-tile-title>
          <v-list-tile-sub-title v-text="item.subtitle"></v-list-tile-sub-title>
        </v-list-tile-content>
        <v-list-tile-action @click="localAddMarker">
          <v-btn
            icon
            ripple
          >
            <v-icon color="grey lighten-1">mdi-map-marker-plus</v-icon>
          </v-btn>
        </v-list-tile-action>
      </template>
    </v-autocomplete>

    <v-btn
      v-if="fullscreenButton"
      flat
      :icon="$vuetify.breakpoint.smAndDown"
      :color="fullscreen ? 'primary' : 'grey darken-1'"
      @click="toggleFullscreen()"
    >
      <v-icon>{{ fullscreen ? 'mdi-fullscreen-exit' : 'fullscreen' }}</v-icon> <span class="hidden-sm-and-down">מסך מלא</span>
    </v-btn>
  </v-toolbar>
</template>

<script>
import { getApi } from "@/api";
import generateId from "@/idGenerator";
import { throttle } from "lodash";

export default {
  data() {
    return {
      test: null,
      query: null,
      select: null,
      searching: false,
      searchResult: null,
      shouldAddMarker: false,
      typesMap: {
        street_address: { icon: null },
        route: { icon: "mdi-road-variant" },
        intersection: { icon: "mdi-sign-direction" },
        political: { icon: "mdi-home-city" },
        country: { icon: "flag" },
        administrative_area_level_1: { icon: "" },
        administrative_area_level_2: { icon: "" },
        administrative_area_level_3: { icon: "" },
        administrative_area_level_4: { icon: "" },
        administrative_area_level_5: { icon: "" },
        colloquial_area: { icon: "" },
        locality: { icon: "mdi-home-city" },
        sublocality: { icon: "mdi-home-group" },
        neighborhood: { icon: "mdi-home-group" },
        premise: { icon: null },
        postal_code: { icon: "mdi-postage-stamp" },
        natural_feature: { icon: "mdi-image-filter-hdr" },
        airport: { icon: "mdi-airport" },
        park: { icon: "mdi-pine-tree" },
        point_of_interest: { icon: null }
      }
    };
  },

  computed: {
    items() {
      if (this.searchResult) {
        let type = this.searchResult.type;
        if (type === "GEOCODE") {
          return this.searchResult.results.map(item => {
            return this.parseGeocode(item);
          });
        } else if (type === "AUTOCOMPLETE") {
          return this.searchResult.results.map(item => {
            return this.parseAutoComplete(item);
          });
        } else if (type === "COORDINATES") {
          return [this.parseCoordinates(this.searchResult)];
        }
      }
      return [];
    }
  },

  methods: {
    search() {
      if (!this.query) {
        this.searchResult = null;
        return;
      }

      this.searching = true;
      getApi()
        .then(api => {
          let params = {
            q: this.query
          };

          return api.get("geo/search", { params });
        })
        .then(({ data }) => {
          this.searchResult = data;
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          this.searching = false;
        });
    },
    throttledSearch: throttle(
      function(self) {
        self.search();
      },
      800,
      { leading: false, trailing: true, maxWait: 5000 }
    ),
    parseCoordinates(item) {
      let { latitude, longitude } = item.result;
      return {
        title: `${latitude.toFixed(5)}/${longitude.toFixed(5)}`,
        subtitle: "קואורדינטות",
        icon: "mdi-crosshairs-gps",
        color: "primary",
        lat: latitude,
        lng: longitude,
        itemType: "COORDINATES",
        _id: generateId()
      };
    },
    parseGeocode(item) {
      let title = item.formatted_address;
      let geometry = item.geometry;
      if (!geometry) {
        return;
      }
      let { lat, lng } = geometry.location;
      let viewport = geometry.viewport;
      let types = item.types;
      let subtitle = types.join(", ");
      let icon = "mdi-map-marker";
      if (Array.isArray(types)) {
        for (let type of types) {
          let typeData = this.typesMap[type];
          if (typeData && typeData.icon) {
            icon = typeData.icon;
            break;
          }
        }
      }
      return {
        title,
        subtitle,
        icon,
        lat,
        lng,
        viewport,
        types,
        itemType: "GEOCODE",
        _id: generateId()
      };
    },
    parseAutoComplete(item) {
      let {
        main_text: title,
        secondary_text: subtitle
      } = item.structured_formatting;
      let types = item.types;
      let icon = "mdi-map-marker";
      if (Array.isArray(types)) {
        for (let type of types) {
          let typeData = this.typesMap[type];
          if (typeData && typeData.icon) {
            icon = typeData.icon;
            break;
          }
        }
      }
      return {
        title,
        subtitle,
        icon,
        types,
        itemType: "AUTOCOMPLETE",
        _id: item.place_id
      };
    },
    googleViewportToLeafletBounds(viewport) {
      let { northeast, southwest } = viewport;
      northeast = viewport.northeast;
      southwest = viewport.southwest;
      let corner1 = [northeast.lat, northeast.lng];
      let corner2 = [southwest.lat, southwest.lng];
      return [corner1, corner2];
    },
    setMapView({ bounds, center, zoom }) {
      if (bounds) {
        this.map.fitBounds(bounds);
      } else if (center && zoom) {
        this.map.setView(center, zoom);
      }
    },
    getPlaceDetails() {
      return new Promise((resolve, reject) => {
        if (!this.select) {
          reject();
        }

        this.searching = true;
        getApi()
          .then(api => {
            let params = {
              placeId: this.select._id,
              language: "he"
            };

            return api.get("geo/place-details", { params });
          })
          .then(({ data }) => {
            resolve(data.result);
            // this.searchResult = data;
          })
          .catch(err => {
            reject(err);
          })
          .finally(() => {
            this.searching = false;
          });
      });
    },
    onSelect(val) {
      console.log('onSelect');
      if (val.itemType === "AUTOCOMPLETE") {
        let placeId = val._id;
        this.getPlaceDetails(placeId)
          .then(data => {
            let geometry = data.geometry;
            if (!geometry) {
              return;
            }
            let { lat, lng } = geometry.location;
            let viewport = geometry.viewport;

            if (this.shouldAddMarker) {
              setTimeout(()=> {
                window.addMarker({ lat, lng });
                this.shouldAddMarker = false;
              }, 200)
            }

            if (viewport) {
              this.setMapView({
                bounds: this.googleViewportToLeafletBounds(viewport)
              });
            } else if (lat && lng) {
              this.setMapView({ center: [lat, lng], zoom: 14 });
            }
          })
          .catch(err => {
            console.log(err);
          });
      } else {
        let { viewport, lat, lng } = val;
        if (viewport) {
          this.setMapView({
            bounds: this.googleViewportToLeafletBounds(viewport)
          });
        } else if (lat && lng) {
          this.setMapView({ center: [lat, lng], zoom: 12 });
        }
      }
    },
    localAddMarker() {
      this.shouldAddMarker = true;
    }
  },

  props: [
    "fullscreen",
    "isSidebarVisible",
    "toggleSidebar",
    "toggleFullscreen",
    "fullscreenButton",
    "map"
  ],

  watch: {
    query(val) {
      this.throttledSearch(this);
    },
    select(val) {
      if (val) {
        this.onSelect(val);
      }
    }
  },

  components: {}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
</style>


// WEBPACK FOOTER //
// src/components/map/MapToolbar.vue